import React from 'react';
import { Link } from 'react-router-dom';

/* ── Capability icons (SVG) ── */
const IconMarket = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
  </svg>
);

const IconRegulatory = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
  </svg>
);

const IconOversight = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
  </svg>
);

const IconCredit = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
  </svg>
);

const IconEconomic = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
  </svg>
);

/* ── Capabilities (condensed, no emojis) ── */
const capabilities = [
  {
    icon: <IconMarket />,
    title: 'Market Intelligence',
    desc: 'Real-time insights into renewable energy markets, investment trends, and portfolio performance.',
  },
  {
    icon: <IconRegulatory />,
    title: 'Regulatory Navigation',
    desc: 'Centralized tracking of green finance regulations across RBI, SEBI, MNRE, and global bodies.',
  },
  {
    icon: <IconOversight />,
    title: 'Performance Oversight',
    desc: 'Continuous monitoring of green loans and investments against financial and ESG targets.',
  },
  {
    icon: <IconCredit />,
    title: 'Credit Risk Assessment',
    desc: 'Specialized scoring frameworks built for the unique risk profiles of green energy projects.',
  },
  {
    icon: <IconEconomic />,
    title: 'Economic Outlook',
    desc: 'Forward-looking financial analysis to support confident funding decisions in clean energy.',
  },
];

/* ── Industries (condensed) ── */
const industries = [
  {
    title: 'Banks & NBFCs',
    desc: 'AI-powered credit risk assessment and post-loan monitoring for green lending portfolios.',
  },
  {
    title: 'Investors & Funds',
    desc: 'Predictive analytics, due diligence automation, and exit strategy optimization for PE/VC.',
  },
  {
    title: 'Government Entities',
    desc: 'Policy impact tracking, ESG measurement, and centralized oversight for public investments.',
  },
  {
    title: 'Insurance Companies',
    desc: 'Climate-risk underwriting, claims prediction, and automated TCFD reporting.',
  },
  {
    title: 'Large Corporates',
    desc: 'ESG compliance dashboards, carbon credit optimization, and green financing advisory.',
  },
];

const Solutions: React.FC = () => {
  return (
    <div className="pt-16 min-h-screen bg-cream">
      {/* Header */}
      <section className="py-20 bg-navy text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-sm font-semibold tracking-widest text-teal uppercase mb-4">Solutions</p>
          <h1 className="text-4xl md:text-5xl font-serif mb-6">
            What we solve
          </h1>
          <p className="text-lg text-white/70 leading-relaxed">
            Five core capabilities across five industry verticals — 
            purpose-built for the institutions financing the energy transition.
          </p>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-sm font-semibold tracking-widest text-teal uppercase mb-3">Capabilities</p>
          <h2 className="text-2xl md:text-3xl font-serif text-navy mb-12">
            Core expertise
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap) => (
              <div
                key={cap.title}
                className="bg-white p-6 rounded-xl border border-gray-100 hover:border-teal/30 hover:shadow-md transition-all duration-300"
              >
                <div className="text-teal mb-3">{cap.icon}</div>
                <h3 className="text-lg font-bold text-navy mb-2">{cap.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-sm font-semibold tracking-widest text-teal uppercase mb-3">Industries</p>
          <h2 className="text-2xl md:text-3xl font-serif text-navy mb-12">
            Who we serve
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((ind) => (
              <div
                key={ind.title}
                className="bg-white p-6 rounded-xl border border-gray-100 hover:border-teal/30 hover:shadow-md transition-all duration-300"
              >
                <h3 className="text-lg font-bold text-navy mb-2">{ind.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-cream">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-serif text-navy mb-4">Ready to explore?</h2>
          <p className="text-gray-600 mb-8">
            See how GIRMP brings these capabilities together into one platform.
          </p>
          <Link
            to="/products/girmp"
            className="inline-block px-8 py-3 bg-navy text-white rounded-lg hover:bg-navy/90 transition-colors font-medium"
          >
            Explore GIRMP →
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
